import { useState } from 'react';
import { AlertTriangle, Trash2, X, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DangerZoneProps {
  title: string;
  description: string;
  confirmationText: string;
  confirmationLabel?: string;
  buttonText?: string;
  onDelete: () => Promise<void>;
  warnings?: string[];
}

export function DangerZone({
  title,
  description,
  confirmationText,
  confirmationLabel = 'Type the name to confirm',
  buttonText = 'Delete',
  onDelete,
  warnings = [],
}: DangerZoneProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmationValid = inputValue === confirmationText;

  const handleOpenModal = () => {
    setShowModal(true);
    setStep(1);
    setInputValue('');
    setError(null);
  };

  const handleClose = () => {
    if (isDeleting) return;
    setShowModal(false);
    setStep(1);
    setInputValue('');
    setError(null);
  };

  const handleProceed = () => {
    setStep(2);
  };

  const handleDelete = async () => {
    if (!isConfirmationValid) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Danger Zone Section */}
      <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full bg-red-50 px-6 py-4 border-b border-red-200 flex items-center justify-between hover:bg-red-100 transition-colors"
        >
          <h2 className="font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </h2>
          <ChevronDown
            className={cn(
              'w-5 h-5 text-red-700 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
        {isExpanded && (
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-medium text-slate-900">{title}</h3>
                <p className="text-sm text-slate-600 mt-1">{description}</p>
              </div>
              <button
                onClick={handleOpenModal}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4" />
                {buttonText}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-100 flex items-center justify-between">
              <h3 className="font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {step === 1 ? 'Are you sure?' : 'Confirm Deletion'}
              </h3>
              <button
                onClick={handleClose}
                disabled={isDeleting}
                className="p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-red-700" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {step === 1 ? (
                <>
                  <p className="text-slate-700 mb-4">{description}</p>

                  {warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <p className="font-medium text-amber-800 mb-2">This action will:</p>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {warnings.map((warning, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-amber-500 mt-0.5">•</span>
                            {warning}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleClose}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceed}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Yes, proceed
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-700 mb-4">
                    This action <strong>cannot be undone</strong>. Please type{' '}
                    <code className="px-2 py-1 bg-slate-100 rounded text-red-600 font-mono text-sm">
                      {confirmationText}
                    </code>{' '}
                    to confirm.
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {confirmationLabel}
                    </label>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={confirmationText}
                      className={cn(
                        'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors',
                        isConfirmationValid
                          ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                          : 'border-slate-300 focus:ring-red-500 focus:border-red-500'
                      )}
                      autoFocus
                      disabled={isDeleting}
                    />
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={!isConfirmationValid || isDeleting}
                      className={cn(
                        'flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2',
                        isConfirmationValid && !isDeleting
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      )}
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete permanently
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
